import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CoinIcon } from './CoinIcon';
import { CoinSparkle } from './CoinSparkle';

/**
 * CoinBurst — the "you just earned N coins" celebration. Dropped into
 * reward moments (quiz completion today, reading challenges tomorrow).
 *
 * Variants:
 *   - `reward-burst` (default) — the big one. Coin springs in, does a
 *     half Y-axis spin, six sparkles arc outward on staggered timers,
 *     and the count text tickers up from 0 → `amount` over ~800 ms.
 *     Full sequence lands at ~1.5 s so it doesn't block the rest of
 *     the review screen for long.
 *   - `tally-fly` — small coin flies from the center of the screen to
 *     the header chip. Caller is responsible for passing a DOM target;
 *     out of scope for v1, declared on the interface so the prop shape
 *     stabilizes early.
 *   - `idle-glimmer` — ambient loop, used as decoration on large hero
 *     coins. Every ~4 s the specular crescent sweeps across.
 *
 * Accessibility: respects `prefers-reduced-motion`. When reduced motion
 * is on, every variant collapses to a simple opacity crossfade — the
 * number still appears, just without the spring + sparkles. The burst
 * also self-announces via a polite live region so keyboard-only and
 * screen-reader users get the reward feedback.
 */
export interface CoinBurstProps {
  /** How many coins were earned. Drives both the text and the duration. */
  amount: number;
  /** Which animation to play. Default: "reward-burst". */
  variant?: 'reward-burst' | 'tally-fly' | 'idle-glimmer';
  /** Called when the full animation sequence completes. */
  onComplete?: () => void;
  /** Overrides the default "+{amount} coins" label. */
  label?: string;
  className?: string;
}

const SPARKLE_COUNT = 6;

export function CoinBurst({
  amount,
  variant = 'reward-burst',
  onComplete,
  label,
  className,
}: CoinBurstProps) {
  const prefersReducedMotion = useReducedMotion();
  const completionFired = useRef(false);

  if (variant === 'idle-glimmer') {
    return <IdleGlimmer className={className} prefersReducedMotion={!!prefersReducedMotion} />;
  }

  // `tally-fly` is reserved for a future tight integration with the
  // header chip. For v1 it's a plain fade — still functional, just not
  // visually elaborate — so callers don't break when they opt into it.
  if (variant === 'tally-fly' || prefersReducedMotion) {
    return (
      <ReducedFallback
        amount={amount}
        label={label}
        className={className}
        onComplete={() => {
          if (completionFired.current) return;
          completionFired.current = true;
          onComplete?.();
        }}
      />
    );
  }

  return (
    <RewardBurst
      amount={amount}
      label={label}
      className={className}
      onComplete={() => {
        if (completionFired.current) return;
        completionFired.current = true;
        onComplete?.();
      }}
    />
  );
}

// -- Reward burst ----------------------------------------------------------

interface BurstBodyProps {
  amount: number;
  label?: string;
  className?: string;
  onComplete: () => void;
}

function RewardBurst({ amount, label, className, onComplete }: BurstBodyProps) {
  // Inline layout: coin glyph and "+N coins" text sit side-by-side. The
  // coin still does a spring-in with a half Y-axis spin, and a compact
  // sparkle burst radiates from it — but the whole thing occupies a
  // single text-line's worth of vertical space so it sits cleanly
  // alongside other success content (medallions, score lines) without
  // dominating the screen.
  const coinSize = 44;
  const burstBox = 96; // enough room for sparkles to arc out beside the coin

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        gap: '10px',
      }}
    >
      {/* Live region — announces once when the burst arrives so
          screen-reader users get the reward feedback. */}
      <span
        role="status"
        aria-live="polite"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
        }}
      >
        {label ?? `You earned ${amount} coin${amount === 1 ? '' : 's'}.`}
      </span>

      <div
        style={{
          position: 'relative',
          width: burstBox,
          height: burstBox,
          flexShrink: 0,
        }}
      >
        {/* Sparkles — radial stars that fan out from the coin. Smaller
            radius than the original celebratory layout so the burst
            stays within the inline footprint. */}
        <AnimatePresence>
          {Array.from({ length: SPARKLE_COUNT }).map((_, i) => {
            const angle = (i / SPARKLE_COUNT) * Math.PI * 2;
            const radius = burstBox / 2 - 8;
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            return (
              <motion.div
                key={`sparkle-${i}`}
                initial={{ opacity: 0, scale: 0.2, x: 0, y: 0 }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  scale: [0.2, 1, 1, 0.6],
                  x: [0, x * 0.6, x, x * 1.1],
                  y: [0, y * 0.6, y, y * 1.1],
                }}
                transition={{
                  duration: 0.9,
                  delay: 0.25 + i * 0.05,
                  ease: 'easeOut',
                  times: [0, 0.3, 0.7, 1],
                }}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginLeft: -6,
                  marginTop: -6,
                }}
              >
                <CoinSparkle size={12} />
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* The coin itself — spring in, one lazy half-spin on Y axis. */}
        <motion.div
          initial={{ opacity: 0, scale: 0.2, rotateY: -180 }}
          animate={{ opacity: 1, scale: 1, rotateY: 0 }}
          transition={{
            type: 'spring',
            stiffness: 220,
            damping: 14,
            mass: 0.8,
            delay: 0.05,
          }}
          onAnimationComplete={() => {
            // Fire onComplete after the count-up would have finished so
            // callers can advance UI state (show medallion, etc.)
            // predictably.
            window.setTimeout(onComplete, 650);
          }}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: coinSize,
            height: coinSize,
            marginLeft: -coinSize / 2,
            marginTop: -coinSize / 2,
            perspective: 600,
          }}
        >
          <CoinIcon size={coinSize} inert />
        </motion.div>
      </div>

      {/* Count-up number — ticks from 0 to `amount`. Keeps the digits
          selectable (plain span, not SVG) so the amount is
          copy-pasteable. */}
      <CountUp
        to={amount}
        durationMs={800}
        delayMs={250}
        label={label ?? `+${amount} coin${amount === 1 ? '' : 's'}`}
      />
    </div>
  );
}

// -- Count-up number -------------------------------------------------------

interface CountUpProps {
  to: number;
  durationMs: number;
  delayMs?: number;
  label: string;
}

function CountUp({ to, durationMs, delayMs = 0, label }: CountUpProps) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    setValue(0);
    if (to <= 0) return;
    let startTime = 0;
    let rafId = 0;
    const step = (timestamp: number) => {
      if (startTime === 0) startTime = timestamp;
      const elapsed = timestamp - startTime;
      if (elapsed < delayMs) {
        rafId = requestAnimationFrame(step);
        return;
      }
      const progress = Math.min(1, (elapsed - delayMs) / durationMs);
      // easeOutCubic — lands softly at `to` without overshoot.
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * to));
      if (progress < 1) {
        rafId = requestAnimationFrame(step);
      }
    };
    rafId = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [to, durationMs, delayMs]);

  return (
    <span
      aria-hidden
      style={{
        fontFamily: '"DM Serif Display", serif',
        fontSize: '28px',
        color: '#B5660F',
        letterSpacing: '0.02em',
      }}
    >
      {label.replace(/\d+/, String(value))}
    </span>
  );
}

// -- Reduced-motion fallback ----------------------------------------------

function ReducedFallback({ amount, label, className, onComplete }: BurstBodyProps) {
  useEffect(() => {
    const timer = window.setTimeout(onComplete, 500);
    return () => window.clearTimeout(timer);
  }, [onComplete]);

  return (
    <div
      className={className}
      role="status"
      aria-live="polite"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 14px',
        borderRadius: '999px',
        backgroundColor: '#FEF3C7',
        color: '#92400E',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        fontSize: '14px',
      }}
    >
      <CoinIcon size={20} inert />
      <span>{label ?? `+${amount} coin${amount === 1 ? '' : 's'}`}</span>
    </div>
  );
}

// -- Idle glimmer ----------------------------------------------------------

function IdleGlimmer({
  className,
  prefersReducedMotion,
}: {
  className?: string;
  prefersReducedMotion: boolean;
}) {
  if (prefersReducedMotion) {
    return (
      <span className={className}>
        <CoinIcon size={64} inert />
      </span>
    );
  }

  return (
    <motion.span
      className={className}
      initial={{ rotateZ: -2 }}
      animate={{ rotateZ: [-2, 2, -2] }}
      transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
      style={{ display: 'inline-block', transformOrigin: 'center center' }}
    >
      <CoinIcon size={64} inert />
    </motion.span>
  );
}
